// (Force rebuild marker removed — Iter 62.4 Bug 5.4)
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { useAIState } from '../contexts/AIStateContext';
import { 
  AlertCircle, AlertTriangle, Coins, Loader2, RefreshCw, Zap, Rocket,
  TrendingUp, Star, Building2, ArrowUpRight, Activity, Calculator
} from 'lucide-react';
import { toast } from 'sonner';
import { handleError } from '../lib/errorHandler';
import { useAuth } from '../contexts/AuthContext';
import { billingAPI, razorpayAPI } from '../lib/api';
import { loadRazorpayScript, openRazorpayOrder, openRazorpaySubscription, detectCountryHint } from '../lib/razorpay';
import { useWebSocket, EventTypes, Channels } from '../hooks/useWebSocket';

// Sub-components
import { BillingOverviewTab } from './billing/BillingOverviewTab';
import { BillingPlansTab } from './billing/BillingPlansTab';
import { BillingCreditsTab } from './billing/BillingCreditsTab';
import { BillingInvoicesTab, BillingPaymentsTab } from './billing/BillingInvoicesPayments';
import { LlmCostTab } from './billing/LlmCostTab';
import { ActionPricingTab } from './billing/ActionPricingTab';
import CreditCalculator from './billing/CreditCalculator';
import { fmtCr, getPlanColor, getStatusBadge, getLiveEligibilityBadge, isCustomPlan } from './billing/billingHelpers';

const getPlanIconEl = (planId) => {
  switch (planId) {
    case 'free': return <Zap className="h-6 w-6" />;
    case 'starter': return <Rocket className="h-6 w-6" />;
    case 'growth': return <TrendingUp className="h-6 w-6" />;
    case 'scale': return <Star className="h-6 w-6" />;
    case 'enterprise': return <Building2 className="h-6 w-6" />;
    default: return <Star className="h-6 w-6" />;
  }
};

export default function Billing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshAIState } = useAIState();
  
  // State
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [billing, setBilling] = useState(null);
  const [reverseTrialDays, setReverseTrialDays] = useState(0);
  const [usage, setUsage] = useState(null);
  const [liveEligibility, setLiveEligibility] = useState(null);
  const [renewalStatus, setRenewalStatus] = useState(null);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loadErrors, setLoadErrors] = useState({ invoices: false, payments: false });
  const [subscriptionInvoices, setSubscriptionInvoices] = useState([]);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [changingPlan, setChangingPlan] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [stripeMode, setStripeMode] = useState(null);
  // Payment gateway routing (India => Razorpay, else => Stripe)
  const [gatewayConfig, setGatewayConfig] = useState(null);
  const gateway = gatewayConfig?.gateway || 'stripe';
  const currency = gatewayConfig?.currency || 'USD';
  // Credits state
  const [credits, setCredits] = useState(null);
  const [creditHistory, setCreditHistory] = useState([]);
  const [creditPurchaseLoading, setCreditPurchaseLoading] = useState(null);
  const [customCreditAmount, setCustomCreditAmount] = useState(0);
  const syncedSessionRef = React.useRef(new Set()); // Prevent duplicate sync calls
  const [creditTransactions, setCreditTransactions] = useState([]);
  const [creditSummary, setCreditSummary] = useState(null);
  const [autoTopup, setAutoTopup] = useState({ enabled: false, threshold: 5.0, amount: 25.0 });
  const [autoTopupSaving, setAutoTopupSaving] = useState(false);
  const [txPage, setTxPage] = useState(0);
  const [txTotal, setTxTotal] = useState(0);
  const [syncLoading, setSyncLoading] = useState(false);
  // Phase 5: controlled top-level Tabs state so credits-badge can programmatically switch tabs
  const normalizeBillingTab = (t) => {
    if (['credits', 'cost-breakdown', 'pricing', 'usage'].includes(t)) return 'usage-costs';
    return ['overview', 'plans', 'usage-costs', 'history'].includes(t) ? t : 'overview';
  };
  const [activeTab, setActiveTab] = useState(normalizeBillingTab(searchParams.get('tab')));
  // Header credit-pill & alert links use legacy ?tab=credits — keep tab in sync
  useEffect(() => {
    setActiveTab(normalizeBillingTab(searchParams.get('tab')));
  }, [searchParams]);  
  // Cost Management state
  const [costDashboard, setCostDashboard] = useState(null);
  const [costLoading, setCostLoading] = useState(false);
  // Usage History state
  const [usageHistoryState, setUsageHistoryState] = useState({ data: null, loading: false });
  const usageHistory = usageHistoryState.data;
  const usageHistoryLoading = usageHistoryState.loading;
  const [historyPeriod, setHistoryPeriod] = useState('week');
  const [historyCategory, setHistoryCategory] = useState('all');
  const [historyDirection, setHistoryDirection] = useState('all');
  const [historySearch, setHistorySearch] = useState('');
  const [historyFrom, setHistoryFrom] = useState('');
  const [historyTo, setHistoryTo] = useState('');
  
  const isAdmin = user?.role === 'admin' || user?.role === 'tenant_admin' || user?.role === 'org_admin' || user?.role === 'superadmin';

  // Phase P4: Real-time credits badge — when the backend fires CREDIT_THRESHOLD_CROSSED
  // (services/credit_service.deduct_credits), reflect the fresh balance immediately.
  // GlobalCreditListener handles the toast — here we only update the in-page badge.
  useWebSocket(
    [Channels.GLOBAL],
    {
      [EventTypes.CREDIT_THRESHOLD_CROSSED]: (data) => {
        if (typeof data?.balance_after === 'number') {
          setBilling((prev) => (prev ? { ...prev, credit_balance_usd: data.balance_after } : prev));
        }
      },
    },
    { showToasts: false, autoConnect: true }
  );
  
  // Fetch Stripe mode on mount
  useEffect(() => {
    const fetchStripeMode = async () => {
      try {
        const response = await billingAPI.getStripeMode();
        setStripeMode(response.data);
      } catch (error) {
        console.error('Failed to fetch Stripe mode:', error);
      }
    };
    fetchStripeMode();
  }, []);

  // Fetch payment gateway config (which gateway + currency to show this buyer)
  useEffect(() => {
    const fetchGateway = async () => {
      try {
        const res = await razorpayAPI.getGatewayConfig(detectCountryHint());
        setGatewayConfig(res.data);
        if (res.data?.gateway === 'razorpay') { loadRazorpayScript(); }
      } catch (error) {
        console.error('Failed to fetch gateway config:', error);
        setGatewayConfig({ gateway: 'stripe', currency: 'USD' });
      }
    };
    fetchGateway();
  }, []);
  
  // Handle checkout return status
  useEffect(() => {
    const status = searchParams.get('status');
    const sessionId = searchParams.get('session_id');
    const portalReturn = searchParams.get('portal');
    const creditPack = searchParams.get('credit_pack');
    const customCredits = searchParams.get('custom_credits');
    const creditPool = searchParams.get('credit_pool');
    const tab = searchParams.get('tab');

    // Committed credit pool return — grant only after Stripe confirms payment
    if (status === 'success' && sessionId && creditPool) {
      if (syncedSessionRef.current.has(sessionId)) {
        navigate('/billing?tab=credits', { replace: true });
        return;
      }
      syncedSessionRef.current.add(sessionId);

      const syncPool = async () => {
        try {
          const res = await billingAPI.syncCreditPool(sessionId);
          if (res.data.synced) {
            toast.success('Credit pool activated!', {
              description: res.data.message || 'Your committed credit pool is now active.'
            });
          } else {
            toast.info('Processing...', {
              description: res.data.message || 'Your payment is being processed. Your pool will appear shortly.'
            });
          }
        } catch (error) {
          console.error('Error syncing credit pool:', error);
          toast.success('Purchase successful!', { description: 'Your credit pool will be added shortly.' });
        }
        loadBillingData();
        navigate('/billing?tab=credits', { replace: true });
      };
      syncPool();
      return;
    }

    // Credit purchase return — guard against React 18 StrictMode double-fire
    if (status === 'success' && sessionId && (creditPack || customCredits)) {
      if (syncedSessionRef.current.has(sessionId)) {
        navigate('/billing?tab=credits', { replace: true });
        return;
      }
      syncedSessionRef.current.add(sessionId);

      const syncCredits = async () => {
        try {
          const syncRes = await billingAPI.syncCreditPurchase(sessionId);
          const syncData = syncRes.data;
          if (syncData.synced) {
            toast.success('Credits Added!', {
              description: syncData.message || `Added ${fmtCr(syncData.credits_added)} Cr to your balance`
            });
          } else {
            toast.info('Processing...', {
              description: syncData.message || 'Your payment is being processed. Credits will appear shortly.'
            });
          }
        } catch (error) {
          console.error('Error syncing credits:', error);
          toast.success('Purchase successful!', { description: 'Your credits will be added shortly.' });
        }
        loadBillingData();
        navigate('/billing?tab=credits', { replace: true });
      };
      syncCredits();
      return;
    }
    
    // Subscription return
    if (status === 'success' && sessionId) {
      const syncSubscription = async () => {
        try {
          const syncRes = await billingAPI.syncAfterCheckout({ session_id: sessionId });
          const syncData = syncRes.data;
          if (syncData.synced) {
            toast.success('Subscription activated!', {
              description: `Your ${syncData.subscription?.plan_id || 'Starter'} plan is now active. ${syncData.tenant?.can_go_live ? 'You can now switch to LIVE mode!' : ''}`
            });
          } else {
            toast.success('Payment successful!', { description: syncData.message || 'Your subscription is being processed.' });
          }
          refreshAIState();
        } catch (error) {
          console.error('Error syncing subscription:', error);
          toast.warning('Payment received', { description: "We're confirming your subscription — this can take a moment. Refresh in a bit if your plan doesn't update." });
          refreshAIState();
        }
        loadBillingData();
      };
      syncSubscription();
      navigate('/billing', { replace: true });
    } else if (status === 'cancel') {
      toast.info('Checkout cancelled', { description: 'No changes were made to your subscription.' });
      navigate(tab ? `/billing?tab=${tab}` : '/billing', { replace: true });
    } else if (portalReturn === 'returned') {
      toast.info('Syncing your changes...', { description: 'Fetching latest subscription state from Stripe.' });
      // Force-sync with Stripe FIRST (portal changes may not have triggered webhook yet)
      const syncAndReload = async () => {
        try {
          await billingAPI.syncStripe();
        } catch (e) {
          console.error('Portal return sync failed:', e);
        }
        loadBillingData();
        refreshAIState();
        toast.success('Subscription updated!', { description: 'Your billing changes are now reflected.' });
      };
      syncAndReload();
      navigate('/billing', { replace: true });
    }
  }, [searchParams, navigate, refreshAIState]);
  
  useEffect(() => {
    if (isAdmin) { loadBillingData(); }
  }, [isAdmin]);

  // ───────── Data loading ─────────
  async function loadBillingData() {
    try {
      const [planRes, plansRes, usageRes, eligRes, invoiceRes, invoicesRes, paymentsRes, subInvRes, creditsRes, historyRes, txRes, summaryRes, topupRes, costRes, renewalRes] = await Promise.allSettled([
        billingAPI.getCurrentPlan(), billingAPI.getPlans(), billingAPI.getUsage(), billingAPI.getLiveEligibility(),
        billingAPI.getInvoicePreview(), billingAPI.getInvoices(), billingAPI.getPayments(), billingAPI.getSubscriptionInvoices(),
        billingAPI.getCredits(), billingAPI.getCreditHistory(), billingAPI.getCreditTransactions(20, 0),
        billingAPI.getCreditSummary(), billingAPI.getAutoTopup(), billingAPI.getCostDashboard(),
        billingAPI.getRenewalPaymentStatus(),
      ]);
      if (planRes.status === 'fulfilled') { setCurrentPlan(planRes.value.data.plan); setBilling(planRes.value.data.billing); setReverseTrialDays(planRes.value.data.reverse_trial_days ?? 0); }
      if (plansRes.status === 'fulfilled') {
        const planOrder = ['free', 'starter', 'growth', 'scale', 'enterprise'];
        setPlans((plansRes.value.data.plans || []).sort((a, b) => planOrder.indexOf(a.plan_id) - planOrder.indexOf(b.plan_id)));
      }
      if (usageRes.status === 'fulfilled') setUsage(usageRes.value.data);
      if (eligRes.status === 'fulfilled') setLiveEligibility(eligRes.value.data);
      if (invoiceRes.status === 'fulfilled') setInvoicePreview(invoiceRes.value.data);
      if (invoicesRes.status === 'fulfilled') setInvoices(invoicesRes.value.data.invoices || []);
      if (paymentsRes.status === 'fulfilled') setPayments(paymentsRes.value.data.payments || []);
      if (subInvRes.status === 'fulfilled') setSubscriptionInvoices(subInvRes.value.data.invoices || []);
      if (creditsRes.status === 'fulfilled') setCredits(creditsRes.value.data);
      if (historyRes.status === 'fulfilled') setCreditHistory(historyRes.value.data.history || []);
      if (txRes.status === 'fulfilled') { setCreditTransactions(txRes.value.data.transactions || []); setTxTotal(txRes.value.data.total || 0); setTxPage(0); }
      if (summaryRes.status === 'fulfilled') setCreditSummary(summaryRes.value.data);
      if (topupRes.status === 'fulfilled') {
        const d = topupRes.value.data;
        setAutoTopup({ enabled: d.auto_topup_enabled || false, threshold: d.auto_topup_threshold || 5.0, amount: d.auto_topup_amount || 25.0 });
      }
      if (costRes.status === 'fulfilled') setCostDashboard(costRes.value.data);
      if (renewalRes.status === 'fulfilled') setRenewalStatus(renewalRes.value.data);
      setLoadErrors({
        invoices: invoicesRes.status === 'rejected' || subInvRes.status === 'rejected',
        payments: paymentsRes.status === 'rejected',
      });
    } catch (error) {
      handleError('Billing', error, { fallbackMessage: 'Failed to load billing data' });
    } finally {
      setLoading(false);
    }
  };

  async function loadUsageHistory(period, category, fromDate = '', toDate = '') {
    setUsageHistoryState({ data: usageHistoryState.data, loading: true });
    try {
      const res = await billingAPI.getUsageHistory(period, category, fromDate, toDate);
      setUsageHistoryState({ data: res.data, loading: false });
    } catch (e) {
      console.error('Usage history load failed:', e);
      setUsageHistoryState({ data: usageHistoryState.data, loading: false });
    }
  }

  const [exportingUsage, setExportingUsage] = useState(false);
  async function handleExportUsage(period, category, fromDate = '', toDate = '') {
    setExportingUsage(true);
    try {
      const res = await billingAPI.exportUsageHistory(period, category, fromDate, toDate);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const label = (fromDate || toDate) ? `${fromDate || 'start'}_to_${toDate || 'now'}` : period;
      a.download = `lockene_credits_${label}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Usage export failed:', e);
    } finally {
      setExportingUsage(false);
    }
  }

  // Low-credit email alert settings
  const [lowAlert, setLowAlert] = useState({ enabled: false, threshold_credits: 500, email: '' });
  const [lowAlertSaving, setLowAlertSaving] = useState(false);
  useEffect(() => {
    billingAPI.getLowBalanceAlert().then(r => setLowAlert(r.data)).catch(() => {});
  }, []);
  async function handleSaveLowAlert() {
    setLowAlertSaving(true);
    try {
      await billingAPI.setLowBalanceAlert(lowAlert.enabled, parseInt(lowAlert.threshold_credits, 10) || 500);
      toast.success('Low-credit alert saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save alert');
    } finally {
      setLowAlertSaving(false);
    }
  }

  // Auto-load usage history once (inline fetch — no state-mutating function is
  // referenced by the effect, which avoids the RC analyzer false positive).
  const historyLoadedRef = React.useRef(false);
  useEffect(() => {
    if (loading || historyLoadedRef.current) return undefined;
    historyLoadedRef.current = true;
    let active = true;
    setUsageHistoryState({ data: null, loading: true });
    billingAPI.getUsageHistory('week', 'all')
      .then((res) => { if (active) setUsageHistoryState({ data: res.data, loading: false }); })
      .catch((e) => { console.error('Usage history load failed:', e); if (active) setUsageHistoryState({ data: null, loading: false }); });
    return () => { active = false; };
  }, [loading]);

  // ───────── Handlers ─────────
  const handleSyncSubscription = async () => {
    try {
      const response = await billingAPI.syncAfterCheckout({});
      const data = response.data;
      if (data.synced) {
        toast.success('Subscription synced!', { description: `Plan: ${data.subscription?.plan_id || 'Unknown'}` });
        loadBillingData(); refreshAIState();
      } else {
        toast.info('No changes needed', { description: data.message || 'Subscription is already synced' });
      }
    } catch (error) {
      toast.error('Failed to sync subscription', { description: error.response?.data?.detail || error.message });
    }
  };

  const handleOpenCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const response = await billingAPI.createCustomerPortal();
      if (response.data.portal_url) { window.location.href = response.data.portal_url; }
      else { toast.error('Failed to open billing portal'); }
    } catch (error) {
      toast.error('Cannot access billing portal', { description: error.response?.data?.detail || 'Unknown error' });
    } finally { setPortalLoading(false); }
  };

  const handleSyncStripe = async () => {
    setSyncLoading(true);
    try {
      const response = await billingAPI.syncStripe();
      const data = response.data;
      if (data.synced) {
        toast.success('Billing synced with Stripe', { description: `Plan: ${data.current_plan} | Status: ${data.stripe_status}` });
        loadBillingData();
        refreshAIState();
      } else {
        toast.info(data.message || 'Nothing to sync');
      }
    } catch (error) {
      toast.error('Sync failed', { description: error.response?.data?.detail || error.message });
    } finally { setSyncLoading(false); }
  };

  // Shared Razorpay one-time credit flow: create order -> open modal -> verify
  const runRazorpayCreditPurchase = async (createOrderFn) => {
    const orderRes = await createOrderFn();
    const order = orderRes.data;
    if (!order?.order_id) { toast.error('Failed to create Razorpay order'); return; }
    try {
      const payment = await openRazorpayOrder({
        order, keyId: order.key_id, name: 'Lockene CRM',
        description: `${order.name} (incl. 18% GST)`,
        prefill: { email: user?.email || '', name: user?.name || '' },
      });
      const verifyRes = await razorpayAPI.verifyCreditPayment({
        razorpay_order_id: order.order_id,
        razorpay_payment_id: payment.razorpay_payment_id,
        razorpay_signature: payment.razorpay_signature,
      });
      if (verifyRes.data?.synced) {
        toast.success('Credits Added!', { description: verifyRes.data.message });
      }
      loadBillingData();
    } catch (e) {
      if (e?.message === 'dismissed') { toast.info('Payment cancelled'); }
      else { toast.error('Payment failed', { description: e.response?.data?.detail || e.message }); }
    }
  };

  const handlePurchaseCredits = async (packId) => {
    setCreditPurchaseLoading(packId);
    try {
      if (gateway === 'razorpay') {
        await runRazorpayCreditPurchase(() => razorpayAPI.createCreditOrder({ pack_id: packId, state_code: gatewayConfig?.buyer_state_code }));
      } else {
        const response = await billingAPI.purchaseCredits({ pack_id: packId });
        if (response.data.checkout_url) { window.location.href = response.data.checkout_url; }
        else { toast.error('Failed to create checkout session'); }
      }
    } catch (error) { handleError('CreditPurchase', error, { fallbackMessage: 'Cannot purchase credits' }); }
    finally { setCreditPurchaseLoading(null); }
  };

  const handleCustomCreditPurchase = async () => {
    if (!customCreditAmount || customCreditAmount < 1) return;
    setCreditPurchaseLoading('custom');
    try {
      if (gateway === 'razorpay') {
        await runRazorpayCreditPurchase(() => razorpayAPI.createCustomCreditOrder({ amount_usd: customCreditAmount }));
      } else {
        const response = await billingAPI.purchaseCustomCredits({ amount_usd: customCreditAmount });
        if (response.data.checkout_url) { window.location.href = response.data.checkout_url; }
        else { toast.error('Failed to create checkout session'); }
      }
    } catch (error) { handleError('CustomCreditPurchase', error, { fallbackMessage: 'Cannot purchase credits' }); }
    finally { setCreditPurchaseLoading(null); }
  };

  const handleSaveAutoTopup = async () => {
    setAutoTopupSaving(true);
    try {
      await billingAPI.setAutoTopup({ enabled: autoTopup.enabled, threshold: autoTopup.threshold, amount: autoTopup.amount });
      toast.success('Auto top-up settings saved!');
    } catch (error) { handleError('AutoTopup', error, { fallbackMessage: 'Failed to save auto top-up settings' }); }
    finally { setAutoTopupSaving(false); }
  };

  const handleStripeCheckout = async (planId, event, interval = 'monthly') => {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    setCheckoutLoading(planId);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) { toast.error('Session expired', { description: 'Please log in again.' }); setCheckoutLoading(null); return; }
      if (gateway === 'razorpay') {
        const res = await razorpayAPI.createSubscription({ plan_id: planId, interval });
        if (!res.data?.subscription_id) { toast.error('Failed to start Razorpay checkout'); return; }
        try {
          await openRazorpaySubscription({
            subscriptionId: res.data.subscription_id, keyId: res.data.key_id, name: 'Lockene CRM',
            description: `${planId} plan (${interval}) · incl. 18% GST`,
            prefill: { email: user?.email || '', name: user?.name || '' },
          });
          const sync = await razorpayAPI.syncSubscription({ subscription_id: res.data.subscription_id });
          if (sync.data?.synced) {
            toast.success('Subscription activated!', { description: `Your ${planId} plan is now active.${sync.data.tenant?.can_go_live ? ' You can switch to LIVE mode.' : ''}` });
          } else {
            toast.info('Payment received', { description: 'Your subscription is activating — refresh in a moment.' });
          }
          refreshAIState();
          loadBillingData();
        } catch (e) {
          if (e?.message === 'dismissed') { toast.info('Checkout cancelled'); }
          else { toast.error('Razorpay checkout failed', { description: e.response?.data?.detail || e.message }); }
        }
        return;
      }
      const response = await billingAPI.createCheckout({ plan_id: planId });
      if (response.data.checkout_url) { window.location.href = response.data.checkout_url; return; }
      else { toast.error('Failed to create checkout session'); }
    } catch (error) {
      const status = error.response?.status;
      const errorDetail = error.response?.data?.detail || error.message || 'Unknown error';
      if (errorDetail.toLowerCase().includes('demo')) { toast.error('Demo Account Limitation', { description: 'Demo tenants cannot subscribe to paid plans.' }); }
      else if (status === 401) { toast.error('Session Expired', { description: 'Please log in again.' }); }
      else if (status === 403) { toast.error('Subscription Not Available', { description: errorDetail }); }
      else if (status === 503) { toast.error('Payment System Unavailable', { description: errorDetail }); }
      else { toast.error('Checkout failed', { description: errorDetail }); }
    } finally { setCheckoutLoading(null); }
  };

  const handleRazorpayUpgrade = async (planId, interval = 'monthly') => {
    setCheckoutLoading(planId);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) { toast.error('Session expired', { description: 'Please log in again.' }); setCheckoutLoading(null); return; }
      const res = await razorpayAPI.upgradeSubscription({ plan_id: planId, interval });
      if (!res.data?.subscription_id) { toast.error('Could not start upgrade'); return; }
      if (res.data.proration_credits > 0) {
        toast.info('Upgrade started', { description: `We'll add ${Number(res.data.proration_credits).toLocaleString()} credits for unused days once your new plan is active.` });
      }
      try {
        await openRazorpaySubscription({
          subscriptionId: res.data.subscription_id, keyId: res.data.key_id, name: 'Lockene CRM',
          description: `Upgrade to ${planId} plan (${interval}) · incl. 18% GST`,
          prefill: { email: user?.email || '', name: user?.name || '' },
        });
        const sync = await razorpayAPI.syncSubscription({ subscription_id: res.data.subscription_id });
        if (sync.data?.synced) {
          toast.success('Upgrade complete!', { description: `Your ${planId} plan is now active. AI is unlocked.` });
        } else {
          toast.info('Payment received', { description: 'Your upgrade is activating — refresh in a moment.' });
        }
        refreshAIState();
        loadBillingData();
      } catch (e) {
        if (e?.message === 'dismissed') { toast.info('Upgrade cancelled', { description: 'Your current plan is unchanged.' }); }
        else { toast.error('Upgrade checkout failed', { description: e.response?.data?.detail || e.message }); }
      }
    } catch (error) {
      toast.error('Upgrade failed', { description: error.response?.data?.detail || error.message || 'Unknown error' });
    } finally { setCheckoutLoading(null); }
  };

  const handlePlanChange = async (planId) => {
    setChangingPlan(true);
    try {
      const response = await billingAPI.changePlan({ plan_id: planId });
      toast.success(`Successfully changed to ${response.data.new_plan_id} plan`, { description: response.data.warning || 'Your new plan is now active' });
      setUpgradeDialogOpen(false); loadBillingData();
    } catch (error) {
      if (error.response?.status === 402) { toast.error('Payment Required', { description: 'Please subscribe through checkout to access paid plans.' }); }
      else { toast.error('Failed to change plan', { description: error.response?.data?.detail || 'Unknown error' }); }
    } finally { setChangingPlan(false); }
  };

  // ───────── Guards ─────────
  if (!isAdmin) {
    return (
      <div className="p-6" data-testid="billing-page">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>Only tenant administrators can access billing settings.</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="billing-page-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Skeleton className="h-48" /><Skeleton className="h-48" /><Skeleton className="h-48" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  // ───────── Render ─────────
  return (
    <div className="p-6 space-y-6" data-testid="billing-page">
      {/* Payment Failed Banner */}
      {billing?.billing_status === 'past_due' && (
        <Alert variant="destructive" data-testid="payment-failed-banner">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Payment Failed</AlertTitle>
          <AlertDescription>Your last payment failed. Please update your payment method to avoid service interruption.</AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Billing & Plans</h1>
          <p className="text-sm text-muted-foreground">Manage your subscription and view usage</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Phase 5: Credits-remaining badge — always visible, color-coded */}
          {billing && (
            (() => {
              const bal = billing.credit_balance_usd || 0;
              const tone = bal > 5 ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                : bal >= 1 ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                : 'bg-red-500/15 text-red-400 border-red-500/30';
              return (
                <button
                  onClick={() => setActiveTab('usage-costs')}
                  className={`text-sm px-3 py-1 rounded-md border transition-colors hover:brightness-125 ${tone}`}
                  data-testid="credits-remaining-badge"
                  title="Click to view usage & costs"
                >
                  <Coins className="inline h-3.5 w-3.5 mr-1" />
                  {fmtCr(bal)} Cr remaining
                </button>
              );
            })()
          )}
          {gateway === 'stripe' && (
            <Button variant="outline" size="sm" onClick={handleSyncStripe} disabled={syncLoading} data-testid="sync-stripe-button">
              <RefreshCw className={`h-4 w-4 mr-2 ${syncLoading ? 'animate-spin' : ''}`} />{syncLoading ? 'Syncing...' : 'Sync with Stripe'}
            </Button>
          )}
          {billing && getStatusBadge(billing.billing_status, Badge)}
          {gateway === 'stripe' && stripeMode && (
            <Badge
              variant="outline"
              title={stripeMode.message || ''}
              className={`text-sm px-3 py-1 ${
                stripeMode.mode === 'LIVE'
                  ? 'bg-green-500/20 text-green-400 border-green-500/30'
                  : stripeMode.mode === 'BLOCKED'
                  ? 'bg-red-500/20 text-red-400 border-red-500/30'
                  : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
              }`}
              data-testid="stripe-mode-badge"
            >
              {stripeMode.mode === 'LIVE'
                ? 'Stripe · LIVE'
                : stripeMode.mode === 'BLOCKED'
                ? 'Stripe · BLOCKED'
                : 'Stripe · SANDBOX (Test)'}
            </Badge>
          )}
          {gateway === 'razorpay' && gatewayConfig?.razorpay && (
            <Badge
              variant="outline"
              title={`Payments are processed in INR via Razorpay (${gatewayConfig.razorpay.mode === 'live' ? 'live' : 'test'} mode).`}
              className={`text-sm px-3 py-1 ${
                gatewayConfig.razorpay.mode === 'live'
                  ? 'bg-green-500/20 text-green-400 border-green-500/30'
                  : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
              }`}
              data-testid="razorpay-mode-badge"
            >
              {gatewayConfig.razorpay.mode === 'live' ? 'Razorpay · LIVE' : 'Razorpay · TEST'}
            </Badge>
          )}
          {liveEligibility && (
            <Badge variant="outline"
              className={`text-sm px-3 py-1 ${liveEligibility.eligible ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-amber-500/20 text-amber-400 border-amber-500/30'}`}
              data-testid="live-eligibility-badge"
            >
              {liveEligibility.eligible ? 'LIVE Mode Eligible' : 'SIMULATED Mode Only'}
            </Badge>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList data-testid="billing-tabs-list">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans">Plans</TabsTrigger>
          <TabsTrigger value="usage-costs" data-testid="tab-usage-costs">
            <Coins className="h-4 w-4 mr-1" />Usage & Costs
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <Activity className="h-4 w-4 mr-1" />Billing History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <BillingOverviewTab
            currentPlan={currentPlan} billing={billing} liveEligibility={liveEligibility} user={user}
            renewalStatus={renewalStatus}
            portalLoading={portalLoading} onUpgrade={() => setUpgradeDialogOpen(true)} onOpenPortal={handleOpenCustomerPortal}
          />
        </TabsContent>

        <TabsContent value="plans">
          <Tabs defaultValue="compare" className="space-y-4">
            <TabsList className="bg-muted/30" data-testid="plans-subtabs">
              <TabsTrigger value="compare" data-testid="subtab-compare-plans">Compare Plans</TabsTrigger>
              <TabsTrigger value="calculator" data-testid="subtab-credit-calculator">
                <Calculator className="h-4 w-4 mr-1" />Credit Calculator
              </TabsTrigger>
            </TabsList>
            <TabsContent value="compare">
              <BillingPlansTab
                plans={plans} currentPlan={currentPlan} billing={billing}
                checkoutLoading={checkoutLoading} changingPlan={changingPlan} portalLoading={portalLoading}
                onStripeCheckout={handleStripeCheckout} onPlanChange={handlePlanChange} onOpenPortal={handleOpenCustomerPortal}
                onRazorpayUpgrade={handleRazorpayUpgrade}
                gateway={gateway} currency={currency} gstRatePct={gatewayConfig?.gst_rate_pct}
                reverseTrialDays={reverseTrialDays}
              />
            </TabsContent>
            <TabsContent value="calculator">
              <CreditCalculator embedded currency={currency} usdInrRate={gatewayConfig?.usd_inr_rate} />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Phase 5: Usage & Costs — merges Credits + LLM Costs + Pricing into one tab w/ sub-tabs */}
        <TabsContent value="usage-costs">
          <Tabs defaultValue="credits" className="space-y-4">
            <TabsList className="bg-muted/30" data-testid="usage-costs-subtabs">
              <TabsTrigger value="credits" data-testid="subtab-credits">Credits & Top-ups</TabsTrigger>
              <TabsTrigger value="cost-breakdown" data-testid="subtab-cost-breakdown">Cost Breakdown</TabsTrigger>
              <TabsTrigger value="pricing" data-testid="subtab-pricing">Action Pricing</TabsTrigger>
            </TabsList>
            <TabsContent value="credits">
              <BillingCreditsTab
                credits={credits} currentPlan={currentPlan} costDashboard={costDashboard}
                usageHistory={usageHistory} usageHistoryLoading={usageHistoryLoading}
                creditPurchaseLoading={creditPurchaseLoading} customCreditAmount={customCreditAmount} setCustomCreditAmount={setCustomCreditAmount}
                autoTopup={autoTopup} setAutoTopup={setAutoTopup} autoTopupSaving={autoTopupSaving}
                historyPeriod={historyPeriod} setHistoryPeriod={setHistoryPeriod} historyCategory={historyCategory} setHistoryCategory={setHistoryCategory}
                historyDirection={historyDirection} setHistoryDirection={setHistoryDirection} historySearch={historySearch} setHistorySearch={setHistorySearch}
                historyFrom={historyFrom} setHistoryFrom={setHistoryFrom} historyTo={historyTo} setHistoryTo={setHistoryTo}
                lowAlert={lowAlert} setLowAlert={setLowAlert} onSaveLowAlert={handleSaveLowAlert} lowAlertSaving={lowAlertSaving}
                onPurchaseCredits={handlePurchaseCredits} onCustomCreditPurchase={handleCustomCreditPurchase}
                onSaveAutoTopup={handleSaveAutoTopup} onLoadUsageHistory={loadUsageHistory}
                onExportUsage={handleExportUsage} exportingUsage={exportingUsage}
                creditSummary={creditSummary}
                gateway={gateway} currency={currency} usdInrRate={gatewayConfig?.usd_inr_rate} gstRatePct={gatewayConfig?.gst_rate_pct}
              />
            </TabsContent>
            <TabsContent value="cost-breakdown"><LlmCostTab /></TabsContent>
            <TabsContent value="pricing"><ActionPricingTab /></TabsContent>
          </Tabs>
        </TabsContent>

        {/* Phase 5: Billing History — merges Invoices + Payments */}
        <TabsContent value="history">
          <Tabs defaultValue="invoices" className="space-y-4">
            <TabsList className="bg-muted/30" data-testid="history-subtabs">
              <TabsTrigger value="invoices" data-testid="subtab-invoices">Invoices</TabsTrigger>
              <TabsTrigger value="payments" data-testid="subtab-payments">Payments</TabsTrigger>
            </TabsList>
            <TabsContent value="invoices">
              <BillingInvoicesTab subscriptionInvoices={subscriptionInvoices} invoices={invoices} currentPlan={currentPlan} billing={billing} gateway={gateway} loadError={loadErrors.invoices} onRetry={loadBillingData} />
            </TabsContent>
            <TabsContent value="payments">
              <BillingPaymentsTab payments={payments} currentPlan={currentPlan} billing={billing} loadError={loadErrors.payments} onRetry={loadBillingData} />
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upgrade Your Plan</DialogTitle>
            <DialogDescription>Choose a plan to unlock more features</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {plans.filter(p => p.plan_id !== currentPlan?.plan_id && p.plan_id !== 'free').map((plan) => (
              <div key={plan.plan_id}
                className="p-4 rounded-lg border border-border hover:border-primary/50 cursor-pointer transition-all"
                onClick={() => {
                  if (isCustomPlan(plan)) { window.location.href = 'mailto:info@lockene.ai?subject=Enterprise Plan Inquiry'; }
                  else { setSelectedPlan(plan); }
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-muted ${getPlanColor(plan.plan_id)}`}>{getPlanIconEl(plan.plan_id)}</div>
                    <div>
                      <h4 className="font-semibold flex items-center gap-2">{plan.name} {getLiveEligibilityBadge(plan, Badge)}</h4>
                      {plan.monthly_price_usd !== null ? (
                        <p className="text-lg font-bold">
                          {currency === 'INR' && plan.monthly_price_inr != null
                            ? `₹${Number(plan.monthly_price_inr).toLocaleString('en-IN')}`
                            : `$${plan.monthly_price_usd}`}
                          <span className="text-sm font-normal">/mo{currency === 'INR' ? ' + GST' : ''}</span>
                        </p>
                      ) : (
                        <p className="text-amber-400">Contact Sales</p>
                      )}
                    </div>
                  </div>
                  {!isCustomPlan(plan) && (
                    (billing?.stripe_subscription_id && billing?.billing_status === 'active') ? (
                      <Button size="sm" data-testid={`dialog-manage-plan-${plan.plan_id}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleOpenCustomerPortal(); }}
                        disabled={portalLoading}
                      >
                        {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArrowUpRight className="h-4 w-4 mr-1" />Manage via Portal</>}
                      </Button>
                    ) : (
                      <Button size="sm" data-testid={`dialog-select-plan-${plan.plan_id}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleStripeCheckout(plan.plan_id, e); }}
                        disabled={checkoutLoading !== null}
                      >
                        {checkoutLoading === plan.plan_id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Subscribe'}
                      </Button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradeDialogOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
